import { useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { FeedbackPanel } from "./components/feedback/FeedbackPanel";
import { Hero } from "./components/layout/Hero";
import { StudentView } from "./components/student/StudentView";
import { useStudent } from "./state/StudentProvider";

const TABS = [
  { id: "writing", label: "Writing" },
  { id: "feedback", label: "Feedback" },
] as const;

type TabId = (typeof TABS)[number]["id"];

export function App() {
  const { state } = useStudent();
  const [activeTab, setActiveTab] = useState<TabId>("writing");
  const tabRefs = useRef<Record<TabId, HTMLButtonElement | null>>({
    writing: null,
    feedback: null,
  });

  const handleTabKeydown = (event: KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = TABS.findIndex((tab) => tab.id === activeTab);
    let nextIndex: number | null = null;

    switch (event.key) {
      case "ArrowLeft":
        nextIndex = (currentIndex - 1 + TABS.length) % TABS.length;
        break;
      case "ArrowRight":
        nextIndex = (currentIndex + 1) % TABS.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = TABS.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = TABS[nextIndex];
    if (!nextTab) return;
    setActiveTab(nextTab.id);
    tabRefs.current[nextTab.id]?.focus();
  };

  return (
    <div className="app-shell">
      <Hero />

      <div
        className="tab-strip"
        role="tablist"
        aria-label="ConText views"
        onKeyDown={handleTabKeydown}
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            ref={(element) => {
              tabRefs.current[tab.id] = element;
            }}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            className="tab-button"
            aria-selected={activeTab === tab.id}
            aria-controls={`panel-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <main>
        <div
          role="tabpanel"
          id="panel-writing"
          aria-labelledby="tab-writing"
          hidden={activeTab !== "writing"}
        >
          {state.status === "error" ? (
            <section className="view-panel active">
              <div className="gate-card">
                <h3>The writing space is unavailable</h3>
                <p>{state.error}</p>
              </div>
            </section>
          ) : (
            <StudentView active />
          )}
        </div>

        <div
          role="tabpanel"
          id="panel-feedback"
          aria-labelledby="tab-feedback"
          hidden={activeTab !== "feedback"}
        >
          <FeedbackPanel />
        </div>
      </main>
    </div>
  );
}
